/**
 * Trade Booking form types and enums
 */

export enum TradeType {
  SPOT = 'SPOT',
  FORWARD = 'FORWARD',
}

export enum Direction {
  BUY = 'BUY',
  SELL = 'SELL',
}

export enum FixingReference {
  USD_OBS = 'USD_OBS',
  UF = 'UF',
  BLOOMBERG_11AM = 'BLOOMBERG_11AM',
  NA = 'N/A',
}

export enum FixingOffset {
  ZERO = '0',
  MINUS_ONE = '-1',
  MINUS_TWO = '-2',
  NA = 'N/A',
}

export enum SettlementType {
  COMPENSACION = 'COMPENSACION',
  ENTREGA_FISICA = 'ENTREGA_FISICA',
}

export interface FixingScheduleEntry {
  date: string;  // YYYY-MM-DD (empty string for new rows)
  realisedRate?: number | null;
  realisedAt?: string | null;
  realisedBy?: string | null;
}

export interface TradeBookingForm {
  // Trade Type
  tradeType: TradeType;

  // Trade Details
  direction: Direction;
  amount: number | null;
  currency1: string;  // ISO code
  price: number | null;
  spotPrice: number | null;
  forwardPoints: number | null;
  currency2: string;  // ISO code
  contraAmount: number | null;  // Calculated

  // Date Fields
  tradeDate: Date;
  valueDate: Date | null;
  expiryDate: Date | null;
  paymentDate: Date | null;
  // Valuta dates for physical delivery (Entrega Física)
  currency1PaymentDate: Date | null;
  currency2PaymentDate: Date | null;

  // Forward-Specific Fields
  maturity: string;  // e.g., "1Y3M2D" or number of days as string
  maturityDays: number | null;  // Calculated from maturity string
  fixingReference: FixingReference;
  fixingOffset: FixingOffset;
  settlementType: SettlementType;
  settlementCurrency: string;  // ISO code, N/A if Entrega Física

  // Forward Asiático (Average Rate Forward)
  isAverageRateForward: boolean;
  averageRateFixingSchedule: FixingScheduleEntry[];

  // Forward Starting
  isForwardStarting: boolean;
  forwardStartingFixingSchedule: FixingScheduleEntry[];
  spotFixingReference: FixingReference;  // Spot observation reference for Forward Starting (separate from NDF fixingReference)

  // Administrative
  tradeId: string | null;  // Will be auto-generated on backend
  notes: string;

  // Classification fields
  grouping1: string;  // Dynamic grouping value (filtered by entity from groupings config)
  grouping2: string;  // Dynamic grouping value (filtered by entity from groupings config)
  grouping3: string;  // Dynamic grouping value (filtered by entity from groupings config)
  hedgeObject: string;  // Free text field for hedge object reference

  // Parties
  ourParty: string;  // Our trading entity name
  ourPartyId: string;  // Our trading entity client ID
  ourPartyTaxId: string;  // Our trading entity tax ID
  counterparty: string;  // Counterparty entity name
  counterpartyId: string;  // Counterparty bank Firestore ID
  counterpartyTaxId: string;  // Counterparty tax ID

}

export interface FormValidationErrors {
  tradeType?: string;
  direction?: string;
  amount?: string;
  currency1?: string;
  price?: string;
  currency2?: string;
  contraAmount?: string;
  tradeDate?: string;
  valueDate?: string;
  expiryDate?: string;
  paymentDate?: string;
  currency1PaymentDate?: string;
  currency2PaymentDate?: string;
  maturity?: string;
  fixingReference?: string;
  fixingOffset?: string;
  settlementType?: string;
  settlementCurrency?: string;
  averageRateFixingSchedule?: string;
  forwardStartingFixingSchedule?: string;
  forwardPoints?: string;
  ourParty?: string;
  counterparty?: string;
  general?: string;  // For form-level errors
}

export interface AllocationRow {
  entityId: string;
  entityName: string;
  entityTaxId: string;
  grouping1: string;
  grouping2: string;
  grouping3: string;
  amount: number | null;
  percentage: number | null;
  hedgeObject: string;
  notes: string;
}

export interface SplitRow {
  grouping1: string;
  grouping2: string;
  grouping3: string;
  amount: number;
  percentage: number;
  hedgeObject: string;
  notes: string;
}

export interface Shortcut {
  id: string;
  name: string;
  tradeType?: TradeType;
  direction?: Direction;
  currency1?: string;
  amount?: number | null;
  currency2?: string;
  ourParty?: string;
  counterparty?: string;
}

/**
 * Default form values
 */
export const getDefaultTradeBookingForm = (systemDate?: Date): TradeBookingForm => ({
  tradeType: TradeType.SPOT,
  direction: Direction.BUY,
  amount: null,
  currency1: '',
  price: null,
  spotPrice: null,
  forwardPoints: null,
  currency2: '',
  contraAmount: null,
  tradeDate: systemDate || new Date(),
  valueDate: null,  // Will be calculated based on trade type
  expiryDate: null,  // Will be calculated based on trade type
  paymentDate: null,  // Will be calculated based on trade type
  currency1PaymentDate: null,  // Valuta date for currency 1 (Entrega Física)
  currency2PaymentDate: null,  // Valuta date for currency 2 (Entrega Física)
  maturity: '',
  maturityDays: null,
  fixingReference: FixingReference.BLOOMBERG_11AM,
  fixingOffset: FixingOffset.ZERO,
  settlementType: SettlementType.ENTREGA_FISICA,
  settlementCurrency: '',
  isAverageRateForward: false,
  averageRateFixingSchedule: [],
  isForwardStarting: false,
  forwardStartingFixingSchedule: [],
  spotFixingReference: FixingReference.USD_OBS,
  tradeId: null,
  notes: '',
  grouping1: '',  // Dynamic classification (from groupings config)
  grouping2: '',  // Dynamic classification (from groupings config)
  grouping3: '',  // Dynamic classification (from groupings config)
  hedgeObject: '',  // Optional hedge object reference
  ourParty: '',
  ourPartyId: '',
  ourPartyTaxId: '',
  counterparty: '',
  counterpartyId: '',
  counterpartyTaxId: '',
});

/**
 * Get default fixing reference based on currency pair
 */
/**
 * Multi-trade draft types (for save/load modal state)
 */
export interface MultiTradeDraftSummary {
  id: string;
  name: string;
  mode: 'normal' | 'allocations' | 'splits';
  rowCount: number;
  createdByName: string;
  updatedAt: string;
  updatedByName: string;
  lockedByName: string | null;
  lockedAt: string | null;
}

export interface MultiTradeDraft extends MultiTradeDraftSummary {
  trades: any[];
  allocationColumns: any[];
  allocations: any[];
  createdBy: string;
  createdAt: string;
}

export interface MultiTradeDraftPayload {
  name: string;
  mode: string;
  trades: any[];
  allocationColumns: any[];
  allocations: any[];
}

export const getDefaultFixingReference = (currency1: string, currency2: string): FixingReference => {
  const pair = `${currency1}/${currency2}`;

  if (pair === 'USD/CLP') {
    return FixingReference.USD_OBS;
  } else if (pair === 'CLF/CLP') {
    return FixingReference.UF;
  } else {
    return FixingReference.BLOOMBERG_11AM;
  }
};
